import React from 'react';
import {
  Typography,
  Box,
  Button,
  Link,
  CircularProgress,
  Container,
} from '@mui/material';
import LoginForm from '../auth/LoginForm';

const LandingTab = ({
  isLoggedIn,
  loading,
  onLogin,
  onCreateUser,
  onWorkOSLogin,
}) => {
  // 2026-06-17: Increment 4 — declutter. The nine writeups and the comparison table move behind
  // two text links; clicking one swaps to a simple back-able reader view. null = home.
  const [view, setView] = React.useState(null); // null | 'guide' | 'comparison'

  return (
    <Box sx={{ bgcolor: '#faf6ef', mx: 'calc(50% - 50vw)', mt: '-5px', mb: '-5px', py: 4, minHeight: '85vh' }}>
    <Container maxWidth="lg" sx={{ textAlign: 'center', px: { xs: 2, sm: 4 } }}>
      {view === null && (
      <>
      {/* Hero Section */}
      <Box sx={{ mt: 3, mb: 4 }}>
        <Typography sx={{ fontFamily: '"Inter", sans-serif', textTransform: 'uppercase', letterSpacing: '0.18em', fontSize: '0.72rem', fontWeight: 600, color: '#6b7280', mb: 1.5 }}>
          RothGPT
        </Typography>
        <Typography variant="h4" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, letterSpacing: '-0.01em', color: '#16263f', mb: 1.5 }}>
          Roth Conversion Analysis — and Nothing Else
        </Typography>
        <Typography variant="body1" sx={{ color: '#3a3f4a', maxWidth: 640, mx: 'auto', lineHeight: 1.6 }}>
          RothGPT does one thing: model your complete retirement account lifecycle to get conversion decisions precisely right. No financial planning suite. No portfolio management. Just the deepest conversion analysis available.
        </Typography>
      </Box>

      {/* Show login form if not logged in */}
      {!isLoggedIn && (
        <Box style={{ marginTop: '0px', marginBottom: '20px' }}>
          <LoginForm
            onLogin={onLogin}
            onCreateUser={onCreateUser}
            onWorkOSLogin={onWorkOSLogin}
          />
        </Box>
      )}

      {/* Show loading spinner if logged in but data is loading */}
      {isLoggedIn && loading && (
        <Box style={{ marginTop: '40px' }}>
          <CircularProgress />
          <Typography>Loading your data...</Typography>
        </Box>
      )}

      {/* Learn-more links — full text lives one click away */}
      <Typography variant="body2" sx={{ mt: 4, color: '#3a3f4a' }}>
        New here? See{' '}
        <Link component="button" type="button" onClick={() => setView('guide')} sx={{ verticalAlign: 'baseline', color: '#1e3a5f', fontWeight: 600, textUnderlineOffset: '2px' }}>
          Why RothGPT Is Different
        </Link>{' '}
        — or{' '}
        <Link component="button" type="button" onClick={() => setView('comparison')} sx={{ verticalAlign: 'baseline', color: '#1e3a5f', fontWeight: 600, textUnderlineOffset: '2px' }}>
          how it compares
        </Link>.
      </Typography>

      {/* Closing navy band — full-bleed, flush to the bottom of the cream area */}
      <Box sx={{ bgcolor: '#16263f', mx: 'calc(50% - 50vw)', mt: 9, mb: '-32px', py: 5, px: 3 }}>
        <Container maxWidth="md" disableGutters>
          <Typography sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, fontSize: '1.5rem', color: '#faf6ef', mb: 1 }}>
            Know exactly what each conversion earns you.
          </Typography>
          <Typography sx={{ color: '#c9d2dd', maxWidth: 600, mx: 'auto', lineHeight: 1.6 }}>
            The distribution tax rate others can&apos;t calculate, the IRR others can&apos;t show — every dollar traced from conversion through beneficiary depletion.
          </Typography>
          <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
            <Typography sx={{ color: '#9aa7b5', fontSize: '0.75rem', mb: 1.5, maxWidth: 560, mx: 'auto', lineHeight: 1.5 }}>
              Educational analysis based on your assumptions — not financial, tax, or legal advice. Consult a professional before acting.
            </Typography>
            <Link href="/privacy.html" sx={{ color: '#c9d2dd', mx: 1.5, fontSize: '0.85rem', textUnderlineOffset: '2px' }}>
              Privacy Policy
            </Link>
            <Link href="/docs.html" sx={{ color: '#c9d2dd', mx: 1.5, fontSize: '0.85rem', textUnderlineOffset: '2px' }}>
              Connector Docs
            </Link>
          </Box>
        </Container>
      </Box>
      </>
      )}

      {view === 'guide' && (
      <>
      <Box sx={{ textAlign: 'left', maxWidth: '800px', mx: 'auto', mb: 1 }}>
        <Button onClick={() => setView(null)} sx={{ textTransform: 'none', color: '#1e3a5f' }}>&larr; Back</Button>
      </Box>
      {/* Feature Sections */}
      <Box sx={{ textAlign: 'left', maxWidth: '800px', mx: 'auto', mt: 2 }}>
        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Full Distribution Lifecycle Modeling
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Every retirement account must fully deplete its assets over a finite life — through owner distributions and the 10-year inherited IRA beneficiary period mandated by the SECURE Act. No other conversion software models this complete lifecycle. Without it, you cannot compute a true distribution tax rate, an accurate IRR, or any meaningful conversion metric. RothGPT simulates every year from conversion through beneficiary depletion, because that's the only way to get the math right.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Quantified Return on Your Tax Payment
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          When you pay conversion tax from outside savings, that payment is an investment — a hidden Roth contribution. RothGPT computes the IRR on that investment: the exact annualized return you earn by paying tax today to receive tax-free distributions tomorrow. No other tool does this. Instead of "convert or don't convert" based on total wealth comparisons, you get a precise return figure you can compare directly against your portfolio return.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Interacting Financial Systems
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Tax brackets, Social Security torpedo, IRMAA Medicare surcharges, RMDs, and tax drag don't exist in isolation — they feed into each other. A conversion changes provisional income, which triggers Social Security taxation, which changes taxable income, which shifts your tax bracket, which changes IRMAA premiums, which changes after-tax cash flow. RothGPT models all of these interactions simultaneously, not in silos.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Short-Term Costs, Long-Term Benefits — Quantified
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Conversions increase current-year income, triggering higher IRMAA Medicare premiums two years out and additional Social Security taxation today. But they reduce future MAGI, lowering IRMAA premiums and distribution taxes for years to come. Other tools bury these effects in total plan projections. RothGPT isolates them — showing the conversion-year cost alongside the future savings as separate line items — so you see the full tradeoff, not just the net.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Time-Weighted Precision
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Most tools apply a single average tax rate across all distribution years. RothGPT computes the actual year-by-year cash flows — capturing SS torpedo effects in early years, bracket transitions, and IRMAA cliff crossings — then discounts each year at its true time value. The result: present values and IRRs that reflect when money actually moves, not simplified averages.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          SS Torpedo-Aware Bracket Sizing
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Other tools show that Social Security taxation affects conversions. RothGPT analytically solves for the exact conversion amount that fits each tax bracket after accounting for the SS torpedo — computing how each dollar of conversion triggers additional taxable Social Security across the 50% and 85% provisional income zones. The conversion amounts you see are precisely sized, not approximations.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Complete Decomposition
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Every conversion is broken into its components — hidden contribution, tax-rate spread, Medicare impact, and tax drag benefit — each shown in both present value and future value. Every metric exists as cumulative and incremental parts, and parts sum exactly to the whole. Nothing is hidden in a black box.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Edge Cases as First-Class Features
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Inside vs. outside tax payment. Fixed annuity vs. RMD distributions. Spouse IRMAA with independent Medicare eligibility ages. Social Security already in payment. Beneficiary period distributions. These aren't afterthoughts — they're built into the core simulation and change the recommendation.
        </Typography>

        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5 }}>
          Transparent and Verifiable
        </Typography>
        <Typography variant="body2" sx={{ mb: 3 }}>
          RothGPT shows its work. The IRR cash flow decomposition table lets you trace every dollar, every year, from conversion tax outflow through distribution deltas to cumulative present value. You can verify that the numbers reconcile — because they do.
        </Typography>
      </Box>

      </>
      )}

      {view === 'comparison' && (
      <>
      <Box sx={{ textAlign: 'left', maxWidth: '900px', mx: 'auto', mb: 1 }}>
        <Button onClick={() => setView(null)} sx={{ textTransform: 'none', color: '#1e3a5f' }}>&larr; Back</Button>
      </Box>
      {/* Feature Comparison Table */}
      <Box sx={{ mt: 2, mb: 4, maxWidth: '900px', mx: 'auto' }}>
        <Typography variant="h6" sx={{ fontFamily: '"Source Serif 4", Georgia, serif', fontWeight: 600, color: '#16263f', mb: 0.5, textAlign: 'center' }}>
          Feature Comparison
        </Typography>
        <Typography variant="body2" sx={{ textAlign: 'center', mb: 1.5, color: '#16263f', fontWeight: 600 }}>
          Every competitor treats Roth conversion as one feature within a broader platform. None treat it as an investment with its own return. None compute distribution tax rates, IRR, or decompose the conversion into components. None model through beneficiary depletion. They answer "should I convert?" with a yes/no wealth comparison. RothGPT answers "what exactly do I earn on each bracket converted, and why?"
        </Typography>
        <Box component="table" sx={{
          width: '100%',
          borderCollapse: 'collapse',
          '& th, & td': {
            border: '1px solid #ddd',
            padding: '6px 10px',
            fontSize: '0.82rem',
            textAlign: 'center',
          },
          '& th': { fontWeight: 'bold', backgroundColor: '#f5f5f5' },
          '& td:first-of-type': { textAlign: 'left', fontWeight: 500 },
        }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left' }}>Feature</th>
              <th>Holistiplan</th>
              <th>Income Lab</th>
              <th>Boldin</th>
              <th>RightCapital</th>
              <th>MaxiFi</th>
              <Box component="th" sx={{ backgroundColor: '#eef1f6 !important', color: '#16263f' }}>RothGPT</Box>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Distribution tax rate (t<sub>D</sub>) calculation</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Tax-rate spread profit/loss</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>IRR on conversion tax payment</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Full lifecycle incl 10-yr beneficiary</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>SS torpedo bracket sizing (analytical)</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Future IRMAA savings from conversion</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Conversion-year IRMAA cost (lookback)</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Component decomposition (HC, spread, IRMAA, drag)</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Time-weighted PV/FV</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr>
              <td>Inside vs outside payment analysis</td>
              <td></td><td></td><td></td><td></td><td></td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr style={{ backgroundColor: '#fafafa' }}>
              <td>Multi-year bracket filling</td>
              <td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr style={{ backgroundColor: '#fafafa' }}>
              <td>IRMAA awareness in planning</td>
              <td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr style={{ backgroundColor: '#fafafa' }}>
              <td>SS taxation in model</td>
              <td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
            <tr style={{ backgroundColor: '#fafafa' }}>
              <td>Total wealth comparison</td>
              <td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td><td>&#10003;</td>
              <td style={{ color: '#2e7d32', fontWeight: 'bold' }}>&#10003;</td>
            </tr>
          </tbody>
        </Box>
      </Box>
      </>
      )}

    </Container>
    </Box>
  );
};

export default LandingTab;
